import { ComponentType } from 'react'
import { CurrentUser } from 'customTypes'
import { LocationPaths, OnboardingPath } from 'location.types'
import { getVideoCallData } from 'screens/VideoCalls/utils'
import { getUserData } from 'utils/userData'
import { SurveysConfigKind, UiFlags, UserSignUpSource } from 'types'
import { getOnboardingData } from './utils/onboardingStorage'

export type OnboardingUiFlags = Pick<UiFlags, 'insuranceInformationSubmitted'>

export interface OnboardingStep {
  path: LocationPaths | OnboardingPath
  location: string
  completedResolver: (user?: CurrentUser, uiFlags?: OnboardingUiFlags | null) => boolean
  canNavigateBack?: boolean
  hideSecurityNote?: boolean
  hideLogo?: boolean
  lazy: () => Promise<{ Component: ComponentType }>
  noPageAnimation?: boolean
}

export const getOnboardingFlow: () => OnboardingStep[] = () => {
  const setPasswordStep: OnboardingStep = {
    path: OnboardingPath.SetPassword,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.SetPassword}`,
    completedResolver: (user) => {
      if (user?.signUpSource !== UserSignUpSource.Checkout) {
        return true
      }

      const onboardingData = getOnboardingData()

      if (onboardingData?.goal) {
        return true
      }

      return onboardingData?.password_updated === true
    },
    canNavigateBack: false,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/SetPassword')).OnboardingSetPassword
    })
  }

  const startStep: OnboardingStep = {
    path: OnboardingPath.Start,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.Start}`,
    completedResolver: (user) => {
      const surveyLink = user?.surveyLinks?.find(
        (link) => link.survey.kind === SurveysConfigKind.OnboardingExperienceBasicInformation
      )
      return surveyLink?.finished || false
    },
    canNavigateBack: false,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/Start')).OnboardingStart
    })
  }

  const onboardingExperienceBasicInformationStep: OnboardingStep = {
    path: OnboardingPath.BasicInformation,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.BasicInformation}`,
    completedResolver: (user) => {
      const surveyLink = user?.surveyLinks?.find(
        (link) => link.survey.kind === SurveysConfigKind.OnboardingExperienceBasicInformation
      )
      return surveyLink?.finished || false
    },
    canNavigateBack: true,
    lazy: async () => {
      const { SurveyScreen } = await import('screens/Survey')
      return {
        Component: () =>
          SurveyScreen({
            surveyKind: SurveysConfigKind.OnboardingExperienceBasicInformation
          })
      }
    },
    noPageAnimation: true
  }

  const goalStep: OnboardingStep = {
    path: OnboardingPath.Goal,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.Goal}`,
    completedResolver: () => {
      // Check if user has a primary goal set
      return !!getOnboardingData()?.goal
    },
    canNavigateBack: false,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/OnboardingGoal')).OnboardingGoal
    })
  }

  const preSchedulingInsuranceProviderStep: OnboardingStep = {
    path: OnboardingPath.PreSchedulingInsuranceProvider,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.PreSchedulingInsuranceProvider}`,
    completedResolver: () => {
      const onboardingData = getOnboardingData()

      return !!onboardingData?.insurance_plan || onboardingData?.call_scheduled === true
    },
    canNavigateBack: true,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/OnboardingInsuranceProvider'))
        .OnboardingInsuranceProvider
    })
  }

  const selectCoachingTeamStep: OnboardingStep = {
    path: OnboardingPath.SelectCoachingTeam,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.SelectCoachingTeam}`,
    completedResolver: () => {
      return getOnboardingData()?.call_scheduled === true
    },
    canNavigateBack: true,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/SelectCoachingTeam')).SelectCoachingTeam
    })
  }

  const selectCoachStep: OnboardingStep = {
    path: OnboardingPath.SelectCoach,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.SelectCoach}`,
    completedResolver: () => {
      return getOnboardingData()?.call_scheduled === true
    },
    canNavigateBack: true,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/SelectCoach')).SelectCoach
    })
  }

  const scheduleCallStep: OnboardingStep = {
    path: OnboardingPath.ScheduleCall,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.ScheduleCall}`,
    completedResolver: () => {
      return getOnboardingData()?.call_scheduled === true
    },
    canNavigateBack: true,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/ScheduleCallStep')).ScheduleCallStep
    })
  }

  const selectStressCoachStep: OnboardingStep = {
    path: OnboardingPath.SelectStressCoach,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.SelectStressCoach}`,
    completedResolver: () => {
      return getOnboardingData()?.call_scheduling_completed === true
    },
    canNavigateBack: false,
    lazy: async () => {
      const { SelectCoach } = await import('screens/Onboarding/SelectCoach')
      return {
        Component: () => SelectCoach({ coachingKind: 'stress' })
      }
    }
  }

  const scheduleStressCallStep: OnboardingStep = {
    path: OnboardingPath.ScheduleStressCall,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.ScheduleStressCall}`,
    completedResolver: () => {
      return getOnboardingData()?.call_scheduling_completed === true
    },
    canNavigateBack: true,
    lazy: async () => {
      const { ScheduleCallStep } = await import('screens/Onboarding/ScheduleCallStep')
      return {
        Component: () => ScheduleCallStep({ coachingKind: 'stress' })
      }
    }
  }

  const insuranceIntroStep: OnboardingStep = {
    path: OnboardingPath.InsuranceIntro,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.InsuranceIntro}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: false,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/InsuranceIntro')).InsuranceIntro
    })
  }

  const insuranceProviderStep: OnboardingStep = {
    path: OnboardingPath.InsuranceProvider,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.InsuranceProvider}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: true,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/OnboardingInsuranceProvider'))
        .OnboardingInsuranceProvider
    })
  }

  const stateStep: OnboardingStep = {
    path: OnboardingPath.State,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.State}`,
    completedResolver: (user) => {
      const userData = getUserData()
      return !!user?.address?.state || !!userData?.state
    },
    canNavigateBack: true,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/SubmitState')).OnboardingSubmitState
    })
  }

  const postSchedulingInsuranceStep: OnboardingStep = {
    path: OnboardingPath.PostSchedulingInsurance,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.PostSchedulingInsurance}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: false,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/OnboardingPostSchedulingInsurance'))
        .OnboardingPostSchedulingInsurance
    })
  }

  const insuranceCheckStep: OnboardingStep = {
    path: OnboardingPath.InsuranceCheck,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.InsuranceCheck}`,
    completedResolver: (_user, uiFlags) => uiFlags?.insuranceInformationSubmitted ?? false,
    canNavigateBack: true,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/InsuranceCheck')).InsuranceCheck
    })
  }

  const insuranceStep: OnboardingStep = {
    path: OnboardingPath.Insurance,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.Insurance}`,
    completedResolver: (_user, uiFlags) => uiFlags?.insuranceInformationSubmitted ?? false,
    canNavigateBack: false,
    lazy: async () => ({
      Component: (await import('screens/InsuranceQuestionnaire')).InsuranceQuestionnaireScreen
    }),
    noPageAnimation: true
  }

  const confirmationStep: OnboardingStep = {
    path: OnboardingPath.Confirmation,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.Confirmation}`,
    completedResolver: () => {
      return false
    },
    canNavigateBack: false,
    lazy: async () => ({
      Component: (await import('screens/Onboarding/Confirmation')).OnboardingConfirmation
    })
  }

  const heardAboutNutrisenseStep: OnboardingStep = {
    path: OnboardingPath.HeardAboutNutrisense,
    location: `${LocationPaths.Onboarding}/${OnboardingPath.HeardAboutNutrisense}`,
    completedResolver: () => !!getVideoCallData()?.heard_about_us,
    canNavigateBack: false,
    lazy: async () => ({
      Component: (await import('screens/VideoCalls/HeardAboutNutrisense'))
        .HeardAboutNutrisenseOnboarding
    })
  }

  return [
    setPasswordStep,
    startStep,
    onboardingExperienceBasicInformationStep,
    goalStep,
    stateStep,
    preSchedulingInsuranceProviderStep,
    selectCoachingTeamStep,
    selectCoachStep,
    scheduleCallStep,
    selectStressCoachStep,
    scheduleStressCallStep,
    postSchedulingInsuranceStep,
    insuranceIntroStep,
    insuranceProviderStep,
    insuranceCheckStep,
    insuranceStep,
    heardAboutNutrisenseStep,
    confirmationStep
  ]
}
